import React, { useState, useEffect } from "react";
import { authFetch } from "../pages/Auth";
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  COLORS, CHART_COLORS, commonAxisStyle, commonGridStyle,
  tooltipStyle, cardStyle, chartHeaderStyle,
} from "../chartTheme";

function useApi(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(path)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);
  return { data, loading, error };
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        border: `3px solid ${COLORS.border}`,
        borderTopColor: COLORS.primary,
        animation: "spin 0.8s linear infinite",
      }} />
    </div>
  );
}

function KPICard({ label, value, sub, color, icon }) {
  return (
    <div style={{ ...cardStyle, position: "relative", overflow: "hidden" }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: color || COLORS.primary, borderRadius: "10px 10px 0 0",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>
        {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || COLORS.text, lineHeight: 1, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textDim }}>{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={chartHeaderStyle}>{title}</div>
      {children}
    </div>
  );
}

export default function DiscontinuationPhase() {
  const candidates = useApi("/api/deprescribing/candidates");
  const depSummary = useApi("/api/deprescribing/summary");
  const weightTrends = useApi("/api/analytics/weight-trends");

  const isLoading = candidates.loading || depSummary.loading || weightTrends.loading;
  if (isLoading) return <Spinner />;

  const candData = candidates.data || {};
  const sumData = depSummary.data || {};
  const wt = weightTrends.data || {};

  // Candidates
  const candidateList = Array.isArray(candData) ? candData : (candData.candidates || []);
  const candidateCount = candidateList.length || candData.total_candidates || 0;

  // Savings
  const potentialSavings = sumData.potential_annual_savings || sumData.annual_savings || null;
  const avgTimeOnTherapy = sumData.avg_time_on_therapy_months || sumData.avg_duration_months || null;

  // Discharge reasons
  const dischargeReasons = sumData.discharge_reasons || wt.discharge_reasons || [];
  const reasonData = Array.isArray(dischargeReasons) ? dischargeReasons : [];

  // Weight regain
  const regainData = wt.regain_monitoring || wt.weight_regain || [];

  // Discharge breakdown pie
  const dischargePie = reasonData.map((r, i) => ({
    name: r.reason || r.discharge_reason || `Reason ${i + 1}`,
    value: r.count || r.patient_count || 0,
    color: CHART_COLORS[i % CHART_COLORS.length],
  })).filter((d) => d.value > 0);

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
        <KPICard
          label="Deprescribing Candidates"
          value={candidateCount}
          sub="Eligible for step-down evaluation"
          color={COLORS.warning}
          icon="📋"
        />
        <KPICard
          label="Potential Annual Savings"
          value={potentialSavings != null ? `$${Number(potentialSavings).toLocaleString()}` : "N/A"}
          sub="If candidates step down"
          color={COLORS.success}
          icon="💰"
        />
        <KPICard
          label="Avg Time on Therapy"
          value={avgTimeOnTherapy != null ? `${avgTimeOnTherapy}mo` : "N/A"}
          sub="Before discontinuation"
          color={COLORS.info}
          icon="📅"
        />
      </div>

      {/* Deprescribing Candidates */}
      <SectionCard title="Deprescribing Candidates">
        {candidateList.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <th style={thStyle}>Patient</th>
                  <th style={thStyle}>Program</th>
                  <th style={thStyle}>Months on Therapy</th>
                  <th style={thStyle}>Readiness Score</th>
                  <th style={thStyle}>Rationale</th>
                </tr>
              </thead>
              <tbody>
                {candidateList.slice(0, 15).map((c, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={tdStyle}>
                      {c.given_name || c.givenName || ""} {c.family_name || c.familyName || ""}
                      {!(c.given_name || c.givenName) && (c.patient_id || c.patientId || `#${i + 1}`)}
                    </td>
                    <td style={tdStyle}>{c.program_name || c.program || "—"}</td>
                    <td style={tdStyle}>{c.months_on_therapy || c.duration_months || "—"}</td>
                    <td style={tdStyle}>
                      <span style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: getScoreColor(c.readiness_score || c.score, 0.15),
                        color: getScoreColor(c.readiness_score || c.score, 1),
                      }}>
                        {c.readiness_score || c.score || "—"}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.rationale || c.reason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {candidateList.length > 15 && (
              <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 8, textAlign: "center" }}>
                Showing 15 of {candidateList.length} candidates
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            No deprescribing candidates identified — data will populate when the deprescribing endpoint is configured
          </div>
        )}
      </SectionCard>

      {/* Discharge Reasons */}
      <SectionCard title="Discharge Reasons Breakdown">
        {dischargePie.length > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
            <ResponsiveContainer width="50%" height={220}>
              <PieChart>
                <Pie data={dischargePie} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" paddingAngle={3}>
                  {dischargePie.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {dischargePie.map((d) => (
                <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                  <span style={{ fontSize: 12, color: COLORS.text, flex: 1 }}>{d.name}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            Discharge reason data unavailable
          </div>
        )}
      </SectionCard>

      {/* Weight Regain Monitoring */}
      <SectionCard title="Weight Regain Monitoring — Discharged Patients">
        {Array.isArray(regainData) && regainData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={regainData}>
              <CartesianGrid {...commonGridStyle} />
              <XAxis dataKey="month" tick={commonAxisStyle} />
              <YAxis tick={commonAxisStyle} unit=" lbs" />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="avg_weight" stroke={COLORS.danger} fill={COLORS.danger} fillOpacity={0.1} strokeWidth={2} name="Avg Weight" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ color: COLORS.textDim, fontSize: 12, textAlign: "center", padding: 30 }}>
            Weight regain data will appear once discharged patients have follow-up observations
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function getScoreColor(score, opacity) {
  if (score == null) return `rgba(138,148,158,${opacity})`;
  const n = typeof score === "number" ? score : parseFloat(score);
  if (n >= 0.7) return `rgba(52,211,153,${opacity})`;
  if (n >= 0.4) return `rgba(251,191,36,${opacity})`;
  return `rgba(255,77,109,${opacity})`;
}

const thStyle = {
  textAlign: "left", padding: "8px 10px", fontSize: 10,
  color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1,
  fontWeight: 600,
};

const tdStyle = {
  padding: "10px 10px", fontSize: 12, color: COLORS.text,
};
